import { getUrl } from '@buffer-mono/shared-utils/bibe'

import {
  CALLBACKS_PRODUCTION_URL,
  MIGRATE_ATTEMPT_COOKIE,
  MIGRATE_ATTEMPT_COOKIE_MAX_AGE_SECONDS,
  MIGRATE_ATTEMPT_COOKIE_ROOT_DOMAIN,
  MIGRATE_ATTEMPT_LIMIT,
  OIDC_DOOR_PATH,
} from './constants'
import { redirectToLegacyLogout } from './redirectToLegacyLogout'
import { redirectToOidcLogout } from './redirectToOidcLogout'

let authDoorCheck: Promise<boolean | undefined> | undefined

function readCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`))
    ?.split('=')[1]
}

export function isRootDomainHost(hostname: string): boolean {
  return (
    hostname === MIGRATE_ATTEMPT_COOKIE_ROOT_DOMAIN ||
    hostname.endsWith(`.${MIGRATE_ATTEMPT_COOKIE_ROOT_DOMAIN}`)
  )
}

function attemptCookieScope(): string {
  return isRootDomainHost(window.location.hostname)
    ? `; domain=.${MIGRATE_ATTEMPT_COOKIE_ROOT_DOMAIN}; secure`
    : ''
}

function recordAttempt(attempts: number): void {
  document.cookie = `${MIGRATE_ATTEMPT_COOKIE}=${attempts + 1}; path=/${attemptCookieScope()}; max-age=${MIGRATE_ATTEMPT_COOKIE_MAX_AGE_SECONDS}; samesite=lax`
}

async function requestAuthDoor(): Promise<boolean | undefined> {
  const attempts = Number(readCookie(MIGRATE_ATTEMPT_COOKIE) ?? '0')
  if (attempts >= MIGRATE_ATTEMPT_LIMIT) return

  let payload: unknown
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 3000)
  try {
    const response = await fetch(
      new URL(OIDC_DOOR_PATH, getUrl(CALLBACKS_PRODUCTION_URL)).href,
      {
        credentials: 'include',
        cache: 'no-store',
        signal: controller.signal,
      },
    )
    if (!response.ok) return
    payload = await response.json()
  } catch {
    return
  } finally {
    window.clearTimeout(timeout)
  }

  if (typeof payload !== 'object' || payload === null) return
  if (
    'ok' in payload &&
    payload.ok === true &&
    'action' in payload &&
    payload.action === 'none' &&
    'hasSessionCookies' in payload &&
    typeof payload.hasSessionCookies === 'boolean'
  ) {
    return payload.hasSessionCookies
  }
  if (!('action' in payload) || payload.action !== 'migrate') return

  recordAttempt(attempts)

  if ('sessionType' in payload && payload.sessionType === 'oidc') {
    redirectToOidcLogout()
  } else {
    redirectToLegacyLogout()
  }
}

/**
 * Must stay a module-scope call: that is what makes it fire once per full page
 * load rather than on every in-app route change.
 */
export function checkAuthDoor(): Promise<boolean | undefined> {
  authDoorCheck ??= requestAuthDoor()
  return authDoorCheck
}
